import {
  BLOCK_SIZE,
  DATA_HIGH_WATER,
  FRAME_SIZE,
  MANIFEST_PAGE_SIZE,
  MAX_BLOCK_RETRIES,
  PROTOCOL_VERSION,
} from '../lib/constants';
import { sha256 } from '../lib/crypto';
import { getTransfer, listTransfers, saveTransfer, type StoredTransfer } from '../lib/storage';
import type { PeerSession } from '../webrtc/peer';
import { createDestination, createDestinationDirectories, destinationFileExists, ensureReadPermission, ensureWritePermission, testDestinationWrite } from './files';
import { decodeFrame, encodeFrame } from './frame';
import type {
  BatchManifest,
  ConflictPolicy,
  ConflictRequest,
  ControlMessage,
  IncomingOffer,
  LocalSource,
  QueueSnapshot,
  SpeedTestResult,
  TransferCapabilities,
  TransferProgress,
} from './types';

interface ManagerEvents {
  onOffer: (offer: IncomingOffer) => void;
  onProgress: (progress: TransferProgress) => void;
  onNotice: (message: string) => void;
  onPeerCapabilities: (capabilities: TransferCapabilities) => void;
  onQueueChange: (queue: QueueSnapshot) => void;
  onConflict: (request: ConflictRequest) => void;
}

interface OutgoingBatch {
  manifest: BatchManifest;
  sources: LocalSource[];
}

interface IncomingAssembly {
  batch: Omit<BatchManifest, 'files'>;
  pageCount: number;
  pages: Map<number, BatchManifest['files']>;
}

interface IncomingBatch {
  offer: IncomingOffer;
  destination?: FileSystemDirectoryHandle;
  acceptedFileIds?: Set<string>;
  conflictPolicy: ConflictPolicy;
  targetHandles: Map<string, FileSystemFileHandle>;
}

interface ReceiveBlock {
  batchId: string;
  fileId: string;
  fileIndex: number;
  blockIndex: number;
  offset: number;
  size: number;
  frameCount: number;
  hash: string;
  data: Uint8Array;
  frames: Set<number>;
}

interface ReceiveFile {
  batchId: string;
  fileId: string;
  fileIndex: number;
  handle: FileSystemFileHandle;
  writable: FileSystemWritableFileStream;
  verifiedOffset: number;
  size: number;
}

interface ReceiveSpeedTest {
  testId: string;
  size: number;
  received: number;
  startedAt: number;
}

type Waiter = { resolve: (message: ControlMessage) => void; reject: (error: Error) => void };

export class TransferManager {
  private peer: PeerSession;
  private roomId: string;
  private role: 'host' | 'guest';
  private events: ManagerEvents;
  private localCapabilities: TransferCapabilities;
  private outgoing = new Map<string, OutgoingBatch>();
  private incoming = new Map<string, IncomingBatch>();
  private assemblies = new Map<string, IncomingAssembly>();
  private waiters = new Map<string, Waiter>();
  private receiveFile?: ReceiveFile;
  private receiveBlock?: ReceiveBlock;
  private hostQueue: string[] = [];
  private hostActive?: string;
  private paused = false;
  private cancelled = false;
  private pauseResolvers: Array<() => void> = [];
  private wakeLock?: WakeLockSentinel;
  private transferStartedAt = 0;
  private transferredBytes = 0;
  private activeBatchId?: string;
  private skippedFiles = new Set<string>();
  private currentSendResultKey?: string;
  private progressByBatch = new Map<string, TransferProgress>();
  private speedReceive?: ReceiveSpeedTest;
  private speedRunning = false;
  private peerHelloReceived = false;

  constructor(
    peer: PeerSession,
    roomId: string,
    role: 'host' | 'guest',
    events: ManagerEvents,
    localCapabilities: TransferCapabilities = { receiveFiles: true, receiveFolders: true, resumable: true, mobile: false },
  ) {
    this.peer = peer;
    this.roomId = roomId;
    this.role = role;
    this.events = events;
    this.localCapabilities = localCapabilities;
    peer.onControl((message) => void this.handleControl(message).catch((error) => {
      this.events.onNotice(error instanceof Error ? error.message : 'The other browser sent an invalid transfer message.');
    }));
    peer.onData((buffer) => void this.handleData(buffer).catch((error) => {
      this.events.onNotice(error instanceof Error ? error.message : 'Incoming transfer data could not be processed.');
    }));
    this.send({ type: 'hello', protocolVersion: PROTOCOL_VERSION, capabilities: this.localCapabilities });
  }

  async offer(sources: LocalSource[], directories: string[] = [], batchId?: string): Promise<string> {
    if (!sources.length && !directories.length) throw new Error('Choose at least one file or folder.');
    const id = batchId ?? crypto.randomUUID();
    if (!/^[A-Za-z0-9_-]{1,64}$/u.test(id)) throw new Error('The transfer identifier is invalid.');
    const files = sources.map((source) => source.manifest);
    const manifest: BatchManifest = {
      id,
      files,
      directories,
      totalBytes: files.reduce((total, file) => total + file.size, 0),
      topLevelNames: [...new Set([...files.map((file) => file.relativePath.split('/')[0]), ...directories.map((path) => path.split('/')[0])])],
      createdAt: Date.now(),
    };
    this.outgoing.set(id, { manifest, sources });
    if (sources.every((source) => source.handle)) {
      await saveTransfer({
        id,
        roomId: this.roomId,
        direction: 'outgoing',
        state: 'offered',
        directories,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        files: sources.map((source) => ({
          fileId: source.manifest.id,
          relativePath: source.manifest.relativePath,
          verifiedOffset: 0,
          size: source.manifest.size,
          lastModified: source.manifest.lastModified,
          sourceHandle: source.handle,
        })),
      });
    }
    await this.publishOffer(manifest);
    this.emitProgress(manifest, 'sending', 'queued', 0, 0, 0);
    return id;
  }

  hasOutgoing(batchId: string): boolean {
    return this.outgoing.has(batchId);
  }

  async restoreOutgoing(): Promise<number> {
    const records = (await listTransfers()).filter((transfer) => transfer.roomId === this.roomId && transfer.direction === 'outgoing' && transfer.state !== 'complete');
    let restored = 0;
    for (const record of records) {
      const sources: LocalSource[] = [];
      for (const storedFile of record.files) {
        if (!storedFile.sourceHandle) continue;
        const file = await storedFile.sourceHandle.getFile().catch(() => undefined);
        if (!file || file.size !== storedFile.size || file.lastModified !== storedFile.lastModified) continue;
        sources.push({
          handle: storedFile.sourceHandle,
          file,
          manifest: {
            id: storedFile.fileId,
            relativePath: storedFile.relativePath,
            size: storedFile.size,
            lastModified: storedFile.lastModified,
            type: file.type,
          },
        });
      }
      if (!sources.length) continue;
      const files = sources.map((source) => source.manifest);
      const manifest: BatchManifest = {
        id: record.id,
        files,
        directories: record.directories ?? [],
        totalBytes: files.reduce((total, file) => total + file.size, 0),
        topLevelNames: [...new Set([...files.map((file) => file.relativePath.split('/')[0]), ...(record.directories ?? []).map((path) => path.split('/')[0])])],
        createdAt: record.createdAt,
      };
      this.outgoing.set(record.id, { manifest, sources });
      await this.publishOffer(manifest);
      this.emitProgress(manifest, 'sending', 'queued', 0, 0, 0);
      restored += 1;
    }
    return restored;
  }

  private async publishOffer(manifest: BatchManifest): Promise<void> {
    const files = manifest.files;
    const pageCount = Math.ceil(files.length / MANIFEST_PAGE_SIZE);
    const batch: Omit<BatchManifest, 'files'> = {
      id: manifest.id,
      directories: manifest.directories,
      totalBytes: manifest.totalBytes,
      topLevelNames: manifest.topLevelNames,
      createdAt: manifest.createdAt,
    };
    this.send({ type: 'offer-start', batch, pageCount });
    for (let page = 0; page < pageCount; page += 1) {
      this.send({ type: 'manifest-page', batchId: manifest.id, page, files: files.slice(page * MANIFEST_PAGE_SIZE, (page + 1) * MANIFEST_PAGE_SIZE) });
    }
    const digest = await sha256(new TextEncoder().encode(JSON.stringify(files)));
    this.send({ type: 'offer-end', batchId: manifest.id, digest });
  }

  async accept(
    batchId: string,
    destination: FileSystemDirectoryHandle,
    options: { fileIds?: string[]; conflictPolicy?: ConflictPolicy } = {},
  ): Promise<void> {
    const batch = this.incoming.get(batchId);
    if (!batch) throw new Error('The file offer is no longer available.');
    await testDestinationWrite(destination);
    const offeredIds = new Set(batch.offer.files.map((file) => file.id));
    const fileIds = options.fileIds ?? [...offeredIds];
    if (new Set(fileIds).size !== fileIds.length || fileIds.some((id) => !offeredIds.has(id))) {
      throw new Error('The selected file list is invalid.');
    }
    if (!fileIds.length && !batch.offer.directories.length) throw new Error('Select at least one file to receive.');
    batch.destination = destination;
    batch.acceptedFileIds = new Set(fileIds);
    batch.conflictPolicy = options.conflictPolicy ?? 'keep-both';
    await createDestinationDirectories(destination, batch.offer.directories);
    batch.offer.status = 'accepted';
    const previous = await getTransfer(batchId);
    await saveTransfer({
      id: batchId,
      roomId: this.roomId,
      direction: 'incoming',
      state: 'offered',
      directories: batch.offer.directories,
      createdAt: previous?.direction === 'incoming' ? previous.createdAt : Date.now(),
      updatedAt: Date.now(),
      files: batch.offer.files.filter((file) => batch.acceptedFileIds!.has(file.id)).map((file) => {
        const checkpoint = previous?.direction === 'incoming'
          ? previous.files.find((item) => item.fileId === file.id && item.size === file.size && item.lastModified === file.lastModified)
          : undefined;
        return {
          fileId: file.id,
          relativePath: file.relativePath,
          verifiedOffset: checkpoint?.verifiedOffset ?? 0,
          size: file.size,
          lastModified: file.lastModified,
          destinationHandle: checkpoint?.destinationHandle,
        };
      }),
    });
    this.send({ type: 'accept', batchId, fileIds });
    this.events.onOffer({ ...batch.offer });
  }

  decline(batchId: string): void {
    const batch = this.incoming.get(batchId);
    if (batch) {
      batch.offer.status = 'declined';
      this.events.onOffer({ ...batch.offer });
    }
    this.send({ type: 'decline', batchId });
  }

  pause(batchId: string): void {
    this.paused = true;
    this.send({ type: 'pause', batchId });
    this.updateProgressState(batchId, 'paused');
    void this.markTransfer(batchId, 'paused');
  }

  resume(batchId: string): void {
    this.paused = false;
    this.pauseResolvers.splice(0).forEach((resolve) => resolve());
    this.send({ type: 'resume', batchId });
    this.updateProgressState(batchId, 'active');
    void this.markTransfer(batchId, 'active');
    void this.requestWakeLock();
  }

  cancel(batchId: string): void {
    this.cancelled = true;
    this.paused = false;
    this.pauseResolvers.splice(0).forEach((resolve) => resolve());
    this.send({ type: 'cancel', batchId, reason: 'Cancelled by a participant.' });
    void this.closeReceiveFile();
    void this.releaseWakeLock();
    if (this.activeBatchId === batchId) this.activeBatchId = undefined;
    this.updateProgressState(batchId, 'cancelled');
  }

  skipCurrentFile(batchId: string): void {
    if (this.receiveFile) this.send({ type: 'skip-file', batchId, fileId: this.receiveFile.fileId });
    void this.closeReceiveFile();
  }

  retry(batchId: string): void {
    if (!this.outgoing.has(batchId)) return;
    this.cancelled = false;
    this.requestQueue(batchId);
  }

  moveQueued(batchId: string, direction: 'up' | 'down'): void {
    if (this.role === 'host') this.moveHostQueue(batchId, direction);
    else this.send({ type: 'queue-move', batchId, direction });
  }

  async runSpeedTest(size = 4 * 1024 * 1024): Promise<SpeedTestResult> {
    if (this.speedRunning || this.speedReceive || this.activeBatchId || this.hostActive || this.hostQueue.length) {
      throw new Error('Run the connection test while no transfer is active.');
    }
    const boundedSize = Math.max(256 * 1024, Math.min(size, 8 * 1024 * 1024));
    const testId = crypto.randomUUID();
    this.speedRunning = true;
    try {
      const readyPromise = this.waitFor(`speed-ready:${testId}`, 15_000);
      this.send({ type: 'speed-test-start', testId, size: boundedSize });
      const ready = await readyPromise;
      if (ready.type === 'speed-test-reject') throw new Error(ready.reason);
      const completePromise = this.waitFor(`speed-complete:${testId}`, 60_000);
      const chunk = new Uint8Array(FRAME_SIZE);
      let sent = 0;
      const startedAt = performance.now();
      while (sent < boundedSize) {
        const length = Math.min(chunk.byteLength, boundedSize - sent);
        if (this.peer.bufferedAmount > DATA_HIGH_WATER) await this.peer.waitForDataBuffer();
        this.peer.sendData(length === chunk.byteLength ? chunk.buffer : chunk.buffer.slice(0, length));
        sent += length;
      }
      const result = await completePromise;
      if (result.type !== 'speed-test-complete') throw new Error('The connection test did not finish correctly.');
      const elapsedSeconds = Math.max(0.001, Math.max(performance.now() - startedAt, result.elapsedMs) / 1_000);
      return { bytes: result.bytes, elapsedSeconds, bytesPerSecond: result.bytes / elapsedSeconds };
    } finally {
      this.speedRunning = false;
      this.grantWaitingBatch();
    }
  }

  async checkpointNow(): Promise<void> {
    const batchId = this.activeBatchId;
    if (!batchId) return;
    const stored = await getTransfer(batchId);
    if (!stored || stored.state === 'complete') return;
    stored.updatedAt = Date.now();
    await saveTransfer(stored);
  }

  private send(message: ControlMessage): void {
    this.peer.sendControl({ protocolVersion: PROTOCOL_VERSION, ...message });
  }

  private async handleControl(value: unknown): Promise<void> {
    const message = value as ControlMessage;
    if (!message || typeof message !== 'object' || typeof message.type !== 'string') return;
    const waiterKey = this.waiterKey(message);
    if (waiterKey && this.waiters.has(waiterKey)) {
      const waiter = this.waiters.get(waiterKey)!;
      this.waiters.delete(waiterKey);
      waiter.resolve(message);
      return;
    }
    switch (message.type) {
      case 'hello':
        if (message.protocolVersion !== PROTOCOL_VERSION) throw new Error('The other browser uses an incompatible version.');
        if (!message.capabilities || !isTransferCapabilities(message.capabilities)) throw new Error('The other browser sent invalid capability information.');
        if (!this.peerHelloReceived) {
          this.peerHelloReceived = true;
          this.events.onPeerCapabilities(message.capabilities);
          // One browser can create its manager first and send its initial hello before
          // the other listener exists. Echoing the first valid hello makes the
          // handshake self-acknowledging without an unbounded retry loop.
          this.send({ type: 'hello', protocolVersion: PROTOCOL_VERSION, capabilities: this.localCapabilities });
        }
        break;
      case 'offer-start':
        this.assemblies.set(message.batch.id, { batch: message.batch, pageCount: message.pageCount, pages: new Map() });
        break;
      case 'manifest-page':
        this.assemblies.get(message.batchId)?.pages.set(message.page, message.files);
        break;
      case 'offer-end':
        await this.finishOffer(message.batchId, message.digest);
        break;
      case 'accept':
        this.applyAcceptedFiles(message.batchId, message.fileIds);
        this.requestQueue(message.batchId);
        break;
      case 'decline':
        this.updateProgressState(message.batchId, 'cancelled');
        this.events.onNotice('The other person declined the file offer.');
        break;
      case 'queue-request':
        if (this.role === 'host') this.enqueueAsHost(message.batchId);
        break;
      case 'queue-grant':
        if (this.outgoing.has(message.batchId)) void this.runOutgoing(message.batchId);
        break;
      case 'queue-done':
        if (this.role === 'host') this.completeHostQueue(message.batchId);
        break;
      case 'queue-move':
        if (this.role === 'host') this.moveHostQueue(message.batchId, message.direction);
        break;
      case 'queue-state':
        if (this.role === 'guest' && isQueueSnapshot(message)) this.events.onQueueChange({ activeBatchId: message.activeBatchId, batchIds: message.batchIds });
        break;
      case 'file-start':
        await this.prepareReceiveFile(message);
        break;
      case 'block-start':
        this.prepareReceiveBlock(message);
        break;
      case 'file-complete':
        await this.finishReceiveFile(message.batchId, message.fileId);
        break;
      case 'batch-complete':
        await this.finishReceiveBatch(message.batchId);
        break;
      case 'pause':
        this.paused = true;
        this.updateProgressState(message.batchId, 'paused');
        void this.markTransfer(message.batchId, 'paused');
        this.events.onNotice('Transfer paused.');
        break;
      case 'resume':
        this.paused = false;
        this.pauseResolvers.splice(0).forEach((resolve) => resolve());
        this.updateProgressState(message.batchId, 'active');
        void this.markTransfer(message.batchId, 'active');
        void this.requestWakeLock();
        this.events.onNotice('Transfer resumed.');
        break;
      case 'cancel':
        this.cancelled = true;
        await this.closeReceiveFile();
        await this.releaseWakeLock();
        if (this.activeBatchId === message.batchId) this.activeBatchId = undefined;
        this.updateProgressState(message.batchId, 'cancelled');
        this.events.onNotice(message.reason || 'Transfer cancelled.');
        break;
      case 'skip-file':
        this.skippedFiles.add(message.fileId);
        if (this.currentSendResultKey) this.resolveWaiter(this.currentSendResultKey, message);
        break;
      case 'speed-test-start':
        this.prepareSpeedTest(message);
        break;
      case 'speed-test-complete':
      case 'speed-test-ready':
      case 'speed-test-reject':
        break;
      case 'error':
        this.events.onNotice(message.message);
        break;
      default:
        break;
    }
  }

  private async finishOffer(batchId: string, digest: string): Promise<void> {
    const assembly = this.assemblies.get(batchId);
    if (!assembly || assembly.pages.size !== assembly.pageCount) throw new Error('The file list was incomplete.');
    const files = [...assembly.pages.entries()].sort(([a], [b]) => a - b).flatMap(([, page]) => page);
    if (await sha256(new TextEncoder().encode(JSON.stringify(files))) !== digest) throw new Error('The file list failed verification.');
    const offer: IncomingOffer = { ...assembly.batch, files, status: 'offered' };
    this.incoming.set(batchId, { offer, conflictPolicy: 'keep-both', targetHandles: new Map() });
    this.assemblies.delete(batchId);
    this.events.onOffer({ ...offer });
  }

  private applyAcceptedFiles(batchId: string, fileIds: string[]): void {
    const outgoing = this.outgoing.get(batchId);
    if (!outgoing) return;
    if (!Array.isArray(fileIds) || fileIds.length > outgoing.manifest.files.length || fileIds.some((id) => typeof id !== 'string' || id.length > 64)) {
      throw new Error('The other browser selected an invalid file list.');
    }
    const selected = new Set(fileIds);
    if (selected.size !== fileIds.length || fileIds.some((id) => !outgoing.manifest.files.some((file) => file.id === id))) {
      throw new Error('The other browser selected an invalid file list.');
    }
    outgoing.sources = outgoing.sources.filter((source) => selected.has(source.manifest.id));
    outgoing.manifest = {
      ...outgoing.manifest,
      files: outgoing.manifest.files.filter((file) => selected.has(file.id)),
      totalBytes: outgoing.manifest.files.filter((file) => selected.has(file.id)).reduce((total, file) => total + file.size, 0),
    };
    this.emitProgress(outgoing.manifest, 'sending', 'queued', 0, 0, 0);
  }

  private requestQueue(batchId: string): void {
    if (this.role === 'host') this.enqueueAsHost(batchId);
    else this.send({ type: 'queue-request', batchId, senderRole: 'guest' });
  }

  private enqueueAsHost(batchId: string): void {
    if (!this.hostQueue.includes(batchId)) this.hostQueue.push(batchId);
    if (!this.hostActive && !this.speedRunning && !this.speedReceive) this.grantNext();
    else this.publishQueue();
  }

  private moveHostQueue(batchId: string, direction: 'up' | 'down'): void {
    const index = this.hostQueue.indexOf(batchId);
    if (index < 0 || batchId === this.hostActive) return;
    const firstMovable = this.hostActive ? 1 : 0;
    const target = direction === 'up' ? Math.max(firstMovable, index - 1) : Math.min(this.hostQueue.length - 1, index + 1);
    if (target === index) return;
    this.hostQueue.splice(index, 1);
    this.hostQueue.splice(target, 0, batchId);
    this.publishQueue();
  }

  private publishQueue(): void {
    const snapshot: QueueSnapshot = { activeBatchId: this.hostActive, batchIds: [...this.hostQueue] };
    this.events.onQueueChange(snapshot);
    this.send({ type: 'queue-state', ...snapshot });
  }

  private prepareSpeedTest(message: Extract<ControlMessage, { type: 'speed-test-start' }>): void {
    if (this.speedReceive || this.speedRunning || this.activeBatchId || this.hostActive || this.hostQueue.length || message.size < 256 * 1024 || message.size > 8 * 1024 * 1024) {
      this.send({ type: 'speed-test-reject', testId: message.testId, reason: 'The other device is busy or the test size is invalid.' });
      return;
    }
    this.speedReceive = { testId: message.testId, size: message.size, received: 0, startedAt: performance.now() };
    window.setTimeout(() => {
      if (this.speedReceive?.testId === message.testId) {
        this.speedReceive = undefined;
        this.grantWaitingBatch();
      }
    }, 60_000);
    this.send({ type: 'speed-test-ready', testId: message.testId });
  }

  private grantNext(): void {
    if (this.speedRunning || this.speedReceive || this.hostActive) return;
    const next = this.hostQueue[0];
    if (!next) return;
    this.hostActive = next;
    this.publishQueue();
    this.send({ type: 'queue-grant', batchId: next });
    if (this.outgoing.has(next)) void this.runOutgoing(next);
  }

  private completeHostQueue(batchId: string): void {
    if (this.hostActive !== batchId) return;
    this.hostQueue = this.hostQueue.filter((id) => id !== batchId);
    this.hostActive = undefined;
    if (this.hostQueue.length) this.grantNext();
    else this.publishQueue();
  }

  private grantWaitingBatch(): void {
    if (this.role === 'host' && !this.hostActive && this.hostQueue.length) this.grantNext();
  }

  private async runOutgoing(batchId: string): Promise<void> {
    const outgoing = this.outgoing.get(batchId);
    if (!outgoing) return;
    this.cancelled = false;
    this.transferStartedAt = performance.now();
    this.transferredBytes = 0;
    this.activeBatchId = batchId;
    await this.requestWakeLock();
    try {
      for (let fileIndex = 0; fileIndex < outgoing.sources.length; fileIndex += 1) {
        if (this.cancelled) break;
        const source = outgoing.sources[fileIndex];
        await this.waitWhilePaused();
        let file = source.file;
        if (source.handle) {
          if (!await ensureReadPermission(source.handle)) throw new Error(`Read permission was lost for ${source.manifest.relativePath}.`);
          file = await source.handle.getFile();
        }
        if (file.size !== source.manifest.size || file.lastModified !== source.manifest.lastModified) {
          throw new Error(`${source.manifest.relativePath} changed and must be offered again.`);
        }
        const readyPromise = this.waitFor(`file-ready:${batchId}:${source.manifest.id}`, 5 * 60_000);
        this.send({ type: 'file-start', batchId, file: source.manifest, fileIndex });
        const ready = await readyPromise;
        if (ready.type === 'file-skip') {
          this.skippedFiles.add(source.manifest.id);
          continue;
        }
        if (ready.type !== 'file-ready') throw new Error('The recipient returned an invalid file response.');
        const completed = await this.sendFile(outgoing.manifest, source, file, fileIndex, ready.resumeOffset);
        if (!completed) continue;
        const fileAck = this.waitFor(`file-complete:${batchId}:${source.manifest.id}`);
        this.send({ type: 'file-complete', batchId, fileId: source.manifest.id });
        await fileAck;
      }
      if (!this.cancelled) {
        const batchAck = this.waitFor(`batch-complete:${batchId}`);
        this.send({ type: 'batch-complete', batchId });
        await batchAck;
        const completedManifest = withoutSkippedFiles(outgoing.manifest, this.skippedFiles);
        this.emitProgress(completedManifest, 'sending', 'complete', completedManifest.files.length - 1, 0, 0);
        outgoing.manifest.files.forEach((file) => this.skippedFiles.delete(file.id));
        await this.markTransfer(batchId, 'complete');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Transfer failed.';
      this.emitProgress(outgoing.manifest, 'sending', 'failed', 0, 0, 0, message);
      await this.markTransfer(batchId, 'failed');
      this.send({ type: 'error', batchId, code: 'TRANSFER_FAILED', message });
    } finally {
      await this.releaseWakeLock();
      if (this.activeBatchId === batchId) this.activeBatchId = undefined;
      if (this.role === 'host') this.completeHostQueue(batchId);
      else this.send({ type: 'queue-done', batchId });
    }
  }

  private async sendFile(manifest: BatchManifest, source: LocalSource, file: File, fileIndex: number, resumeOffset: number): Promise<boolean> {
    let offset = Math.max(0, Math.min(resumeOffset, file.size));
    let blockIndex = Math.floor(offset / BLOCK_SIZE);
    while (offset < file.size && !this.cancelled) {
      await this.waitWhilePaused();
      const block = new Uint8Array(await file.slice(offset, Math.min(file.size, offset + BLOCK_SIZE)).arrayBuffer());
      const hash = await sha256(block);
      const frameCount = Math.ceil(block.byteLength / FRAME_SIZE);
      let accepted = false;
      for (let retry = 0; retry < MAX_BLOCK_RETRIES && !accepted; retry += 1) {
        const ready = this.waitFor(`block-ready:${manifest.id}:${source.manifest.id}:${blockIndex}`);
        this.send({ type: 'block-start', batchId: manifest.id, fileId: source.manifest.id, fileIndex, blockIndex, offset, size: block.byteLength, frameCount, hash });
        await ready;
        for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
          const payload = block.subarray(frameIndex * FRAME_SIZE, Math.min(block.byteLength, (frameIndex + 1) * FRAME_SIZE));
          if (this.peer.bufferedAmount > DATA_HIGH_WATER) await this.peer.waitForDataBuffer();
          this.peer.sendData(encodeFrame(fileIndex, blockIndex, frameIndex, payload));
        }
        this.currentSendResultKey = `block-result:${manifest.id}:${source.manifest.id}:${blockIndex}`;
        const response = await this.waitFor(this.currentSendResultKey);
        this.currentSendResultKey = undefined;
        if (response.type === 'skip-file') {
          return false;
        }
        accepted = response.type === 'block-ack';
      }
      if (!accepted) throw new Error(`A block of ${source.manifest.relativePath} failed verification repeatedly.`);
      offset += block.byteLength;
      blockIndex += 1;
      this.transferredBytes += block.byteLength;
      this.emitProgress(manifest, 'sending', 'active', fileIndex, offset, file.size);
      await this.updateCheckpoint(manifest.id, source.manifest.id, offset);
    }
    return !this.skippedFiles.has(source.manifest.id);
  }

  private async prepareReceiveFile(message: Extract<ControlMessage, { type: 'file-start' }>): Promise<void> {
    const batch = this.incoming.get(message.batchId);
    if (!batch?.destination) throw new Error('Choose a destination before receiving files.');
    if (batch.acceptedFileIds && !batch.acceptedFileIds.has(message.file.id)) {
      this.send({ type: 'file-skip', batchId: message.batchId, fileId: message.file.id, reason: 'The file was not selected.' });
      return;
    }
    if (this.activeBatchId !== message.batchId) {
      this.activeBatchId = message.batchId;
      this.transferStartedAt = performance.now();
      this.transferredBytes = 0;
      await this.requestWakeLock();
    }
    await this.closeReceiveFile();
    const stored = await getTransfer(message.batchId);
    const checkpoint = stored?.files.find((file) => file.fileId === message.file.id);
    let handle = checkpoint?.destinationHandle;
    let resumeOffset = checkpoint?.verifiedOffset ?? 0;
    if (handle && !await ensureWritePermission(handle)) handle = undefined;
    if (!handle) {
      let policy: Exclude<ConflictPolicy, 'ask'> = batch.conflictPolicy === 'ask'
        ? 'keep-both'
        : batch.conflictPolicy;
      if (batch.conflictPolicy === 'ask' && await destinationFileExists(batch.destination, message.file.relativePath)) {
        policy = await new Promise((resolve) => this.events.onConflict({
          batchId: message.batchId,
          fileId: message.file.id,
          relativePath: message.file.relativePath,
          resolve,
        }));
      }
      const target = await createDestination(batch.destination, message.file.relativePath, policy);
      if (target.skipped || !target.handle) {
        this.skippedFiles.add(message.file.id);
        this.send({ type: 'file-skip', batchId: message.batchId, fileId: message.file.id, reason: 'An existing destination file was kept.' });
        return;
      }
      handle = target.handle;
      resumeOffset = 0;
    } else {
      resumeOffset = Math.max(0, resumeOffset - BLOCK_SIZE);
    }
    const writable = await handle.createWritable({ keepExistingData: true });
    if (resumeOffset === 0) await writable.truncate(0);
    await writable.seek(resumeOffset);
    this.receiveFile = { batchId: message.batchId, fileId: message.file.id, fileIndex: message.fileIndex, handle, writable, verifiedOffset: resumeOffset, size: message.file.size };
    batch.targetHandles.set(message.file.id, handle);
    await this.storeIncomingCheckpoint(message.batchId, message.file.id, handle, resumeOffset);
    this.send({ type: 'file-ready', batchId: message.batchId, fileId: message.file.id, resumeOffset });
  }

  private prepareReceiveBlock(message: Extract<ControlMessage, { type: 'block-start' }>): void {
    if (!this.receiveFile || this.receiveFile.fileId !== message.fileId || this.receiveFile.fileIndex !== message.fileIndex) {
      this.send({ type: 'error', batchId: message.batchId, code: 'FILE_STATE', message: 'Received data for an unexpected file.' });
      return;
    }
    this.receiveBlock = {
      ...message,
      data: new Uint8Array(message.size),
      frames: new Set(),
    };
    this.send({ type: 'block-ready', batchId: message.batchId, fileId: message.fileId, blockIndex: message.blockIndex });
  }

  private async handleData(buffer: ArrayBuffer): Promise<void> {
    if (this.speedReceive) {
      const test = this.speedReceive;
      test.received += buffer.byteLength;
      if (test.received >= test.size) {
        this.speedReceive = undefined;
        this.send({
          type: 'speed-test-complete',
          testId: test.testId,
          bytes: test.size,
          elapsedMs: Math.max(1, performance.now() - test.startedAt),
        });
        this.grantWaitingBatch();
      }
      return;
    }
    const block = this.receiveBlock;
    if (!block) return;
    try {
      const frame = decodeFrame(buffer);
      if (frame.fileIndex !== block.fileIndex || frame.blockIndex !== block.blockIndex || frame.frameIndex >= block.frameCount) {
        throw new Error('A binary frame did not match the active file block.');
      }
      if (block.frames.has(frame.frameIndex)) return;
      const offset = frame.frameIndex * FRAME_SIZE;
      if (offset + frame.payload.byteLength > block.data.byteLength) throw new Error('A binary frame exceeded its block.');
      block.data.set(frame.payload, offset);
      block.frames.add(frame.frameIndex);
      if (block.frames.size === block.frameCount) await this.finishReceiveBlock(block);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid binary frame.';
      this.send({ type: 'block-retry', batchId: block.batchId, fileId: block.fileId, blockIndex: block.blockIndex, reason: message });
      this.receiveBlock = undefined;
    }
  }

  private async finishReceiveBlock(block: ReceiveBlock): Promise<void> {
    this.receiveBlock = undefined;
    if (await sha256(block.data) !== block.hash) {
      this.send({ type: 'block-retry', batchId: block.batchId, fileId: block.fileId, blockIndex: block.blockIndex, reason: 'SHA-256 mismatch.' });
      return;
    }
    const receiveFile = this.receiveFile!;
    await receiveFile.writable.write({ type: 'write', position: block.offset, data: block.data });
    receiveFile.verifiedOffset = block.offset + block.size;
    await this.storeIncomingCheckpoint(block.batchId, block.fileId, receiveFile.handle, receiveFile.verifiedOffset);
    this.transferredBytes += block.size;
    const batch = this.incoming.get(block.batchId)!;
    const manifest = batch.offer.files[receiveFile.fileIndex];
    this.emitProgress(batch.offer, 'receiving', 'active', receiveFile.fileIndex, receiveFile.verifiedOffset, manifest.size);
    this.send({ type: 'block-ack', batchId: block.batchId, fileId: block.fileId, blockIndex: block.blockIndex, verifiedOffset: receiveFile.verifiedOffset });
  }

  private async finishReceiveFile(batchId: string, fileId: string): Promise<void> {
    if (!this.receiveFile || this.receiveFile.fileId !== fileId) return;
    await this.receiveFile.writable.truncate(this.receiveFile.size);
    await this.receiveFile.writable.close();
    this.receiveFile = undefined;
    this.send({ type: 'file-complete-ack', batchId, fileId });
  }

  private async finishReceiveBatch(batchId: string): Promise<void> {
    await this.closeReceiveFile();
    const batch = this.incoming.get(batchId);
    if (batch) {
      batch.offer.status = 'complete';
      this.events.onOffer({ ...batch.offer });
      const completedManifest = withoutSkippedFiles(batch.offer, this.skippedFiles);
      this.emitProgress(completedManifest, 'receiving', 'complete', Math.max(0, completedManifest.files.length - 1), 0, 0);
      batch.offer.files.forEach((file) => this.skippedFiles.delete(file.id));
      await this.markTransfer(batchId, 'complete');
    }
    this.send({ type: 'batch-complete-ack', batchId });
    if (this.activeBatchId === batchId) this.activeBatchId = undefined;
    await this.releaseWakeLock();
  }

  private waiterKey(message: ControlMessage): string | undefined {
    switch (message.type) {
      case 'file-ready':
      case 'file-skip': return `file-ready:${message.batchId}:${message.fileId}`;
      case 'block-ready': return `block-ready:${message.batchId}:${message.fileId}:${message.blockIndex}`;
      case 'block-ack':
      case 'block-retry': return `block-result:${message.batchId}:${message.fileId}:${message.blockIndex}`;
      case 'file-complete-ack': return `file-complete:${message.batchId}:${message.fileId}`;
      case 'batch-complete-ack': return `batch-complete:${message.batchId}`;
      case 'speed-test-ready':
      case 'speed-test-reject': return `speed-ready:${message.testId}`;
      case 'speed-test-complete': return `speed-complete:${message.testId}`;
      default: return undefined;
    }
  }

  private waitFor(key: string, timeoutMs = 30_000): Promise<ControlMessage> {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.waiters.delete(key);
        reject(new Error('The other browser stopped responding.')); 
      }, timeoutMs);
      this.waiters.set(key, {
        resolve: (message) => { window.clearTimeout(timeout); resolve(message); },
        reject: (error) => { window.clearTimeout(timeout); reject(error); },
      });
    });
  }

  private resolveWaiter(key: string, message: ControlMessage): void {
    const waiter = this.waiters.get(key);
    if (!waiter) return;
    this.waiters.delete(key);
    waiter.resolve(message);
  }

  private waitWhilePaused(): Promise<void> {
    if (!this.paused) return Promise.resolve();
    return new Promise((resolve) => this.pauseResolvers.push(resolve));
  }

  private emitProgress(
    manifest: BatchManifest,
    direction: 'sending' | 'receiving',
    state: TransferProgress['state'],
    fileIndex: number,
    fileBytes: number,
    fileSize: number,
    error?: string,
  ): void {
    const seconds = this.transferStartedAt > 0 ? Math.max(0.001, (performance.now() - this.transferStartedAt) / 1000) : 0;
    const progress: TransferProgress = {
      batchId: manifest.id,
      direction,
      state,
      currentFile: manifest.files[fileIndex]?.relativePath,
      fileIndex,
      fileCount: manifest.files.length,
      fileBytes,
      fileSize,
      transferredBytes: state === 'complete' ? manifest.totalBytes : this.transferredBytes,
      totalBytes: manifest.totalBytes,
      bytesPerSecond: seconds > 0 ? this.transferredBytes / seconds : 0,
      elapsedSeconds: seconds,
      completedAt: state === 'complete' ? Date.now() : undefined,
      error,
    };
    this.progressByBatch.set(manifest.id, progress);
    this.events.onProgress(progress);
  }

  private updateProgressState(batchId: string, state: TransferProgress['state']): void {
    const current = this.progressByBatch.get(batchId);
    if (!current) return;
    const progress = { ...current, state };
    this.progressByBatch.set(batchId, progress);
    this.events.onProgress(progress);
  }

  private async storeIncomingCheckpoint(batchId: string, fileId: string, handle: FileSystemFileHandle, offset: number): Promise<void> {
    const stored = await getTransfer(batchId);
    if (!stored) return;
    const file = stored.files.find((item) => item.fileId === fileId);
    if (file) {
      file.destinationHandle = handle;
      file.verifiedOffset = offset;
    }
    stored.state = 'active';
    stored.updatedAt = Date.now();
    await saveTransfer(stored);
  }

  private async updateCheckpoint(batchId: string, fileId: string, offset: number): Promise<void> {
    const stored = await getTransfer(batchId);
    if (!stored) return;
    const file = stored.files.find((item) => item.fileId === fileId);
    if (file) file.verifiedOffset = offset;
    stored.state = 'active';
    stored.updatedAt = Date.now();
    await saveTransfer(stored);
  }

  private async markTransfer(batchId: string, state: StoredTransfer['state']): Promise<void> {
    const stored = await getTransfer(batchId);
    if (!stored) return;
    stored.state = state;
    stored.updatedAt = Date.now();
    await saveTransfer(stored);
  }

  private async closeReceiveFile(): Promise<void> {
    if (!this.receiveFile) return;
    await this.receiveFile.writable.close().catch(() => undefined);
    this.receiveFile = undefined;
    this.receiveBlock = undefined;
  }

  private async requestWakeLock(): Promise<void> {
    if (!navigator.wakeLock || document.visibilityState !== 'visible' || (this.wakeLock && !this.wakeLock.released)) return;
    const wakeLock = await navigator.wakeLock.request('screen').catch(() => undefined);
    if (!wakeLock) return;
    this.wakeLock = wakeLock;
    wakeLock.addEventListener('release', () => {
      if (this.wakeLock === wakeLock) this.wakeLock = undefined;
    }, { once: true });
  }

  private async releaseWakeLock(): Promise<void> {
    await this.wakeLock?.release().catch(() => undefined);
    this.wakeLock = undefined;
  }
}

export function isTransferCapabilities(value: unknown): value is TransferCapabilities {
  if (!value || typeof value !== 'object') return false;
  const capabilities = value as Partial<TransferCapabilities>;
  return typeof capabilities.receiveFiles === 'boolean'
    && typeof capabilities.receiveFolders === 'boolean'
    && typeof capabilities.resumable === 'boolean'
    && typeof capabilities.mobile === 'boolean';
}

function isQueueSnapshot(value: { batchIds?: unknown; activeBatchId?: unknown }): value is QueueSnapshot {
  return Array.isArray(value.batchIds)
    && value.batchIds.length <= 1_000
    && value.batchIds.every((id) => typeof id === 'string' && id.length <= 64)
    && (value.activeBatchId === undefined || typeof value.activeBatchId === 'string');
}

function withoutSkippedFiles(manifest: BatchManifest, skipped: Set<string>): BatchManifest {
  const files = manifest.files.filter((file) => !skipped.has(file.id));
  return { ...manifest, files, totalBytes: files.reduce((total, file) => total + file.size, 0) };
}
